import { useState } from 'react';
import { Play, Plus, Rocket, Timer } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Can } from '@/components/shared/can';
import { TableSkeleton } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useBurndown,
  useCompleteSprint,
  useSaveSprint,
  useSprints,
  useStartSprint,
  useVelocity,
} from './api';
import { SPRINT_STATUS_LABELS } from './labels';
import type { Sprint } from './types';

export function SprintsPanel({ projectId }: { projectId: string }) {
  const { data: sprints = [], isLoading } = useSprints(projectId);
  const start = useStartSprint(projectId);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState<Sprint | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const active = sprints.find((sprint) => sprint.status === 'ACTIVE');
  const chartSprintId = selected ?? active?.id ?? sprints[0]?.id ?? null;

  if (isLoading) return <TableSkeleton columns={3} />;

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Can permission="project.sprint.create">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            New sprint
          </Button>
        </Can>
      </div>

      {sprints.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Rocket className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No sprints yet. Work can sit in the backlog until there is one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sprints.map((sprint) => (
            <Card
              key={sprint.id}
              className={cn(
                'cursor-pointer transition-shadow hover:shadow-brand',
                chartSprintId === sprint.id && 'ring-2 ring-primary/30',
              )}
              onClick={() => setSelected(sprint.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-medium">{sprint.name}</h3>
                    {sprint.goal && (
                      <p className="truncate text-xs text-muted-foreground">{sprint.goal}</p>
                    )}
                  </div>
                  <Badge
                    variant={
                      sprint.status === 'ACTIVE'
                        ? 'default'
                        : sprint.status === 'COMPLETED'
                          ? 'success'
                          : 'outline'
                    }
                  >
                    {SPRINT_STATUS_LABELS[sprint.status]}
                  </Badge>
                </div>

                <p className="mt-2 text-xs text-muted-foreground">
                  {formatDate(sprint.startDate)} – {formatDate(sprint.endDate)}
                  {sprint.capacityHours > 0 && ` · ${sprint.capacityHours}h capacity`}
                </p>

                <div className="mt-3 flex items-center gap-3">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${sprint.percentComplete ?? 0}%` }}
                    />
                  </div>
                  <span className="tabular text-xs text-muted-foreground">
                    {sprint.doneCount ?? 0}/{sprint.itemCount ?? 0}
                  </span>
                </div>

                <Can permission="project.sprint.update">
                  <div className="mt-3 flex gap-2" onClick={(event) => event.stopPropagation()}>
                    {sprint.status === 'PLANNED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={start.isPending}
                        onClick={() => start.mutate(sprint.id)}
                      >
                        <Play className="h-3.5 w-3.5" />
                        Start
                      </Button>
                    )}
                    {sprint.status === 'ACTIVE' && (
                      <Button size="sm" variant="outline" onClick={() => setCompleting(sprint)}>
                        Complete
                      </Button>
                    )}
                  </div>
                </Can>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {chartSprintId && <BurndownChart projectId={projectId} sprintId={chartSprintId} />}
      <VelocityChart projectId={projectId} />

      {adding && <SprintDialog projectId={projectId} onClose={() => setAdding(false)} />}
      {completing && (
        <CompleteDialog
          projectId={projectId}
          sprint={completing}
          sprints={sprints}
          onClose={() => setCompleting(null)}
        />
      )}
    </div>
  );
}

function BurndownChart({ projectId, sprintId }: { projectId: string; sprintId: string }) {
  const { data } = useBurndown(projectId, sprintId);
  if (!data) return null;

  const max = Math.max(data.committedHours, ...data.points.map((point) => point.actual ?? 0), 1);
  const width = 640;
  const height = 200;
  const stepX = data.points.length > 1 ? width / (data.points.length - 1) : width;
  const y = (value: number) => height - (value / max) * height;

  const path = (key: 'ideal' | 'actual') =>
    data.points
      .map((point, index) => ({ point, index }))
      .filter(({ point }) => point[key] !== null)
      .map(({ point, index }, position) =>
        `${position === 0 ? 'M' : 'L'} ${index * stepX} ${y(point[key] as number)}`,
      )
      .join(' ');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Burndown — {data.name}</CardTitle>
        <CardDescription>
          The ideal line runs across every calendar day of the sprint, so a flat weekend reads as a
          weekend rather than as the team falling behind. The actual line stops at today: this is a
          record, not a forecast.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Mini label="Committed" value={`${data.committedHours}h`} />
          <Mini label="Items" value={`${data.doneItems}/${data.committedItems}`} />
          <Mini label="Capacity" value={`${data.capacityHours}h`} />
          <Mini
            label="Added mid-sprint"
            value={String(data.scopeCreepItems)}
            tone={data.scopeCreepItems > 0 ? 'warning' : undefined}
          />
        </div>

        <div className="overflow-x-auto">
          <svg viewBox={`0 0 ${width} ${height + 24}`} className="h-56 w-full min-w-[32rem]">
            <line
              x1={0}
              y1={height}
              x2={width}
              y2={height}
              className="stroke-border"
              strokeWidth={1}
            />
            <path
              d={path('ideal')}
              fill="none"
              className="stroke-muted-foreground/40"
              strokeWidth={2}
              strokeDasharray="4 4"
            />
            <path d={path('actual')} fill="none" className="stroke-primary" strokeWidth={2.5} />
            {data.points.map((point, index) =>
              point.actual === null ? null : (
                <circle
                  key={point.date}
                  cx={index * stepX}
                  cy={y(point.actual)}
                  r={3}
                  className="fill-primary"
                />
              ),
            )}
          </svg>
        </div>

        <div className="mt-2 flex items-center gap-5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-5 bg-primary" />
            Actual remaining
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-5 border-t-2 border-dashed border-muted-foreground/40" />
            Ideal
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function VelocityChart({ projectId }: { projectId: string }) {
  const { data } = useVelocity(projectId);
  if (!data || data.sprints.length === 0) return null;

  const max = Math.max(...data.sprints.map((sprint) => sprint.completedPoints), 1);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Velocity</CardTitle>
        <CardDescription>
          Points completed in the last {data.sprints.length}{' '}
          {data.sprints.length === 1 ? 'sprint' : 'sprints'}. Average {data.averagePoints}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex h-40 items-end gap-3">
          {data.sprints.map((sprint) => (
            <div key={sprint.sprintId} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex w-full flex-1 items-end">
                <div
                  className="w-full rounded-t bg-primary/80"
                  style={{ height: `${(sprint.completedPoints / max) * 100}%` }}
                  title={`${sprint.completedPoints} points, ${sprint.completedItems} items`}
                />
              </div>
              <span className="tabular text-xs font-medium">{sprint.completedPoints}</span>
              <span className="w-full truncate text-center text-[10px] text-muted-foreground">
                {sprint.name}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SprintDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const save = useSaveSprint(projectId);
  const [form, setForm] = useState({ name: '', goal: '', startDate: '', endDate: '', capacityHours: 0 });
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New sprint</DialogTitle>
          <DialogDescription>
            Only one sprint runs at a time per project — two make the velocity of both meaningless.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.name}
              placeholder="Sprint 14"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Goal</Label>
            <Input
              value={form.goal}
              placeholder="What this sprint is for"
              onChange={(e) => setForm({ ...form, goal: e.target.value })}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Starts</Label>
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ends</Label>
              <Input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Capacity (h)</Label>
              <Input
                type="number"
                min={0}
                value={form.capacityHours}
                onChange={(e) => setForm({ ...form, capacityHours: Number(e.target.value) })}
              />
            </div>
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || !form.name || !form.startDate || !form.endDate}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({
                  name: form.name,
                  goal: form.goal || undefined,
                  startDate: form.startDate,
                  endDate: form.endDate,
                  capacityHours: form.capacityHours || undefined,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save the sprint.',
                );
              }
            }}
          >
            Create sprint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({
  projectId,
  sprint,
  sprints,
  onClose,
}: {
  projectId: string;
  sprint: Sprint;
  sprints: Sprint[];
  onClose: () => void;
}) {
  const complete = useCompleteSprint(projectId);
  const [moveTo, setMoveTo] = useState<'BACKLOG' | 'NEXT_SPRINT'>('BACKLOG');
  const [nextSprintId, setNextSprintId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidates = sprints.filter(
    (candidate) => candidate.id !== sprint.id && candidate.status === 'PLANNED',
  );
  const unfinished = (sprint.itemCount ?? 0) - (sprint.doneCount ?? 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Complete {sprint.name}</DialogTitle>
          <DialogDescription>
            {unfinished > 0
              ? `${unfinished} ${unfinished === 1 ? 'item is' : 'items are'} unfinished. They are never marked done — say where they go.`
              : 'Everything committed is finished.'}
          </DialogDescription>
        </DialogHeader>

        {unfinished > 0 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Unfinished work goes to</Label>
              <Select value={moveTo} onChange={(e) => setMoveTo(e.target.value as never)}>
                <option value="BACKLOG">Back to the backlog</option>
                <option value="NEXT_SPRINT" disabled={candidates.length === 0}>
                  The next sprint
                </option>
              </Select>
            </div>
            {moveTo === 'NEXT_SPRINT' && (
              <div className="space-y-1.5">
                <Label>Which sprint</Label>
                <Select value={nextSprintId} onChange={(e) => setNextSprintId(e.target.value)}>
                  <option value="">Choose a sprint</option>
                  {candidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <FormError message={error} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              complete.isPending || (moveTo === 'NEXT_SPRINT' && !nextSprintId && unfinished > 0)
            }
            onClick={async () => {
              setError(null);
              try {
                await complete.mutateAsync({
                  sprintId: sprint.id,
                  moveTo,
                  nextSprintId: moveTo === 'NEXT_SPRINT' ? nextSprintId : undefined,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError
                    ? caught.message
                    : 'Could not complete the sprint.',
                );
              }
            }}
          >
            <Timer className="h-4 w-4" />
            Complete sprint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Mini({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warning';
}) {
  return (
    <div className="rounded-lg border p-2.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('tabular mt-0.5 font-semibold', tone === 'warning' && 'text-amber-600')}>
        {value}
      </div>
    </div>
  );
}
